/**
 * The hands-off scheduling coordinator — the one place that owns "what is this
 * card doing between Convert and the edge picking it up".
 *
 * WHAT THIS IS
 *   The wiring that turns the five unwired primitives (`ScheduleRetryPolicy`,
 *   `BatchScheduleGate`, `IdempotencyKeyRegistry`, `queuedCardReducer`,
 *   `StalledWatchdog`) into behaviour the user can see. It holds, for the life
 *   of the tab: the shared 4-slot gate with its batch-wide `Retry-After` pause,
 *   one attempt nonce per card, one `QueuedCardState` per card, one watchdog
 *   timer per card, and the "Retry all" entry point.
 *
 * WHY IT EXISTS (evidence, 2026-09-05 Mongo write-stall incident)
 *   During the stall one user's 340-file batch fanned out as 340 concurrent
 *   schedule calls (uncapped `Promise.all`) at an already-stalled primary. Each
 *   failure ran `removeFile` FIRST (file-process-editor.component.tsx:205),
 *   which fires `GET /file-manager/delete/<id>` on the upload, process and
 *   download domains — so the bytes were gone before the "Connection Issue"
 *   dialog offered a Retry that could no longer work (correction 16). The blind
 *   `retry(2, 1000)` ladder burned ~92 s of wall clock (correction 17) with no
 *   `Retry-After` and no `Idempotency-Key`, and 303 of 11,882 upload uuids ended
 *   up with duplicate workflows in 24 h. Nothing ever concluded "scheduled but
 *   never started": the pod poll never gives up (correction 22).
 *
 *   Reviewer findings answered here: SaaS #1 (invert the error branch), #2 (cap
 *   the fan-out, share one Retry-After gate), #3 (expected-start watchdog),
 *   #5 (queued/stalled UX with one Retry all), #7 (contract-level 503/409).
 *
 * DESIGN NOTE — why a module singleton and not an `@injectable()` service
 *   xconvert-web's services are one import away from a strongly-connected
 *   component, and a class referenced at DECORATION time (`@inject(X)`) while
 *   its module is mid-evaluation is what TDZ-crashes the Next.js SSG build while
 *   leaving jest green (2026-06 refresh-token incident). This module takes the
 *   same shape as `utils/gif-estimate.store`: leaf-like, no service imports, a
 *   subscribe/notify store the components read directly. The only outbound edge
 *   is `utils/metric-emit`, which resolves `MetricsService` at CALL time.
 *
 * PLAN SECTION
 *   MONGO-STALL-PLAN-v5.2.md §4.1-§4.5, §6 (rollout step 5), §7 metric 5;
 *   corrections 16-22. CORE-PIPELINE-REFERENCE.md §10 ("Schedule",
 *   "Failure table", "Progress").
 */
import { MetricEventName } from '../../types/metric-events';
import { emitMetric } from '../../utils/metric-emit';
import {
    BatchScheduleGate,
    DEFAULT_SCHEDULE_CONCURRENCY,
    ScheduleTier,
    videoConcurrencyForTier,
} from './batch-schedule-gate';
import { IdempotencyKeyRegistry, buildIdempotencyKey } from './idempotency-key';
import {
    INITIAL_QUEUED_CARD_STATE,
    QueuedCardState,
    QueuedCardStatus,
    queuedCardReducer,
} from './queued-card-state';
import { SCHEDULE_RETRY_BUDGET_MS } from './schedule-retry-policy';
import { StalledWatchdog } from './stalled-watchdog';

/**
 * Plan §4.3: the deadline is SERVER-provided (`expectedStartBySeconds` on the
 * schedule response) because the client cannot know queue depth. 90 s is the
 * fallback when an older professorx answers without it — comfortably above the
 * measured pickup latency (p50 0.92 s / p90 1.44 s / p99 8.1 s / max 86 s) so it
 * cannot fire during ordinary busy periods.
 */
export const DEFAULT_EXPECTED_START_SECONDS = 90;

/** What the card and the batch header render from. */
export interface ScheduleCardView {
    status: QueuedCardStatus;
    attempt: number;
    maxAttempts: number;
    /** True once the batch-level "Retry all" is the only way forward. */
    stalled: boolean;
    /** One line of copy, already in the user's words. */
    message: string;
    reason: string | null;
}

export interface ScheduleBatchSummary {
    /** Cards waiting for the edge to start: queued or mid-retry. */
    queued: number;
    /** Cards the edge has actually started and not finished. */
    processing: number;
    /** Cards admitted to the gate whose schedule call has not been sent yet. */
    waiting: number;
    /** Cards that gave up waiting and need one "Retry all". */
    stalled: number;
    /** True while the shared Retry-After pause is holding the batch. */
    paused: boolean;
    /** True while the tab is hidden and nothing new is sent (code 10 (b)). */
    hidden: boolean;
}

/** Re-dispatch one card's schedule call. Installed by `FileProcessService`. */
export type ScheduleDispatcher = (fileUploadId: string) => void;

type Listener = () => void;

interface CardRecord {
    state: QueuedCardState;
    startedAtMs: number;
    /** Set once the edge reports progress; drives the watchdog verdict. */
    lastProgressAtMs: number | null;
    expectedStartAtMs: number | null;
    watchdogHandle: any;
    /** True once the card completed or was scheduled-and-started. */
    settled: boolean;
}

/** The slice of `document` the visibility pause reads (code 10 (b)); structural so tests can fake it. */
export interface VisibilityDocument {
    hidden: boolean;
    addEventListener(type: 'visibilitychange', listener: () => void): void;
    removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface ScheduleCoordinatorOptions {
    now?: () => number;
    setTimer?: (fn: () => void, ms: number) => any;
    clearTimer?: (handle: any) => void;
    concurrency?: number;
    /** Code 10 (b). Defaults to the global `document` when there is one; `null` binds nothing. */
    document?: VisibilityDocument | null;
    /** Injectable so the specs do not have to reach into the metrics container. */
    emit?: (event: MetricEventName, attributes: Record<string, string | number | boolean>) => void;
    mintNonce?: () => string;
    budgetMs?: number;
}

export class ScheduleCoordinator {
    /**
     * `featureFlags.clientQueuedState`. Written once by `FileProcessService`
     * when it reads the flag, read by the components (which have no
     * SettingsService edge of their own). FALSE means every method below is
     * inert and the schedule path is byte-for-byte today's behaviour.
     */
    public enabled = false;

    readonly gate: BatchScheduleGate;

    private readonly now: () => number;
    private readonly setTimer: (fn: () => void, ms: number) => any;
    private readonly clearTimer: (handle: any) => void;
    private readonly emit: (event: MetricEventName, attributes: Record<string, string | number | boolean>) => void;
    private readonly nonces: IdempotencyKeyRegistry;
    private readonly watchdog: StalledWatchdog;
    private readonly budgetMs: number;

    private readonly cards = new Map<string, CardRecord>();
    private readonly listeners = new Set<Listener>();
    private dispatcher: ScheduleDispatcher | null = null;
    private tierValue: ScheduleTier = 'FREE';
    private visibilityDoc: VisibilityDocument | null = null;
    private readonly onVisibilityChange = () => {
        if (!this.visibilityDoc) return;
        this.gate.setHidden(this.visibilityDoc.hidden === true);
        this.notify();
    };

    constructor(options: ScheduleCoordinatorOptions = {}) {
        this.now = options.now ?? (() => Date.now());
        this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
        this.clearTimer = options.clearTimer ?? (handle => clearTimeout(handle));
        this.emit = options.emit ?? ((event, attributes) => emitMetric(event, attributes));
        this.budgetMs = options.budgetMs ?? SCHEDULE_RETRY_BUDGET_MS;
        this.gate = new BatchScheduleGate({
            concurrency: options.concurrency ?? DEFAULT_SCHEDULE_CONCURRENCY,
            now: this.now,
            setTimer: this.setTimer,
            clearTimer: this.clearTimer,
        });
        this.nonces = new IdempotencyKeyRegistry({ mintNonce: options.mintNonce });
        this.watchdog = new StalledWatchdog({ now: this.now, budgetMs: this.budgetMs });
        const doc = options.document === undefined
            ? (typeof document !== 'undefined' ? document as unknown as VisibilityDocument : null)
            : options.document;
        this.bindVisibility(doc);
    }

    // ------------------------------------------------------------ code 10

    /**
     * Code 10 (a): the sold tier, published by whoever already knows the account (`_app.tsx` on
     * `onUserDetailsChanged`). VIDEO work follows it (FREE 4 / BASIC 4 / PRO 8); non-video work
     * keeps the 4-slot gate. Unknown = FREE.
     */
    setTier(tier: ScheduleTier | null | undefined): void {
        this.tierValue = tier ?? 'FREE';
        this.gate.setVideoConcurrency(videoConcurrencyForTier(this.tierValue));
    }

    get tier(): ScheduleTier {
        return this.tierValue;
    }

    /**
     * Code 10 (b): while the tab is hidden the gate starts nothing, so the retry ladder parks and
     * an abandoned tab stops submitting; it resumes where it stopped when the tab is visible again.
     * Idempotent; `null` unbinds.
     */
    bindVisibility(doc: VisibilityDocument | null): void {
        if (this.visibilityDoc === doc) return;
        if (this.visibilityDoc) {
            try { this.visibilityDoc.removeEventListener('visibilitychange', this.onVisibilityChange); } catch { /* fake docs */ }
        }
        this.visibilityDoc = doc;
        if (!doc) {
            this.gate.setHidden(false);
            return;
        }
        try {
            doc.addEventListener('visibilitychange', this.onVisibilityChange);
        } catch { /* a document without events: read once below */ }
        this.gate.setHidden(doc.hidden === true);
    }

    /** True while the tab is hidden and the gate is holding every not-yet-sent call. */
    get pausedByVisibility(): boolean {
        return this.gate.hidden;
    }

    // ---------------------------------------------------------------- store

    subscribe(listener: Listener): () => void {
        this.listeners.add(listener);
        return () => { this.listeners.delete(listener); };
    }

    /** Whether the queued machine has anything to say about this card. */
    has(fileUploadId: string): boolean {
        return this.cards.has(fileUploadId);
    }

    /** Null when the card is untracked or the machine is idle about it. */
    view(fileUploadId: string): ScheduleCardView | null {
        const record = this.cards.get(fileUploadId);
        if (!record || record.state.status === 'idle') return null;
        const { status, attempt, maxAttempts, reason } = record.state;
        return {
            status,
            attempt,
            maxAttempts,
            stalled: status === 'stalled' || status === 'retry-all-available',
            message: scheduleCardMessage(record.state),
            reason,
        };
    }

    summary(): ScheduleBatchSummary {
        let queued = 0;
        let processing = 0;
        let stalled = 0;
        this.cards.forEach(record => {
            const status = record.state.status;
            if (status === 'queued' || status === 'retrying') queued++;
            else if (status === 'stalled' || status === 'retry-all-available') stalled++;
            else if (!record.settled && record.lastProgressAtMs !== null) processing++;
        });
        return {
            queued,
            processing,
            waiting: this.gate.queued,
            stalled,
            paused: this.gate.isPaused,
            hidden: this.gate.hidden,
        };
    }

    /** Every card currently offered by "Retry all". */
    stalledCardIds(): string[] {
        const ids: string[] = [];
        this.cards.forEach((record, id) => {
            if (record.state.status === 'stalled' || record.state.status === 'retry-all-available') {
                ids.push(id);
            }
        });
        return ids;
    }

    // ------------------------------------------------------------- nonces

    /**
     * The `Idempotency-Key` for this card's CURRENT attempt:
     * `${fileUpload.id}:${attemptNonce}`.
     *
     * `persistedNonce` is the value read back off the FileUpload (which is
     * itself serialised into localStorage), so a tab reloaded mid-retry resumes
     * with the SAME key and professorx replays the first outcome instead of
     * creating the duplicate workflow the key exists to prevent (correction 18).
     */
    keyFor(fileUploadId: string, persistedNonce?: string | null): string {
        if (persistedNonce && !this.nonces.has(fileUploadId)) {
            return buildIdempotencyKey(fileUploadId, this.adoptNonce(fileUploadId, persistedNonce));
        }
        return this.nonces.keyFor(fileUploadId);
    }

    /** The nonce alone, for persisting onto the card. */
    nonceFor(fileUploadId: string, persistedNonce?: string | null): string {
        this.keyFor(fileUploadId, persistedNonce);
        return this.nonces.nonceFor(fileUploadId);
    }

    /** A user-pressed Retry mints a NEW nonce — never dedup'd against a failure. */
    renewNonce(fileUploadId: string): string {
        this.nonces.renewFor(fileUploadId);
        return this.nonces.nonceFor(fileUploadId);
    }

    private adoptNonce(fileUploadId: string, nonce: string): string {
        return this.nonces.adopt(fileUploadId, nonce);
    }

    // -------------------------------------------------------- transitions

    /** Convert pressed for this card. Idempotent for a card already tracked. */
    begin(fileUploadId: string): void {
        if (!fileUploadId) return;
        const existing = this.cards.get(fileUploadId);
        if (existing) {
            existing.settled = false;
            return;
        }
        this.cards.set(fileUploadId, {
            state: INITIAL_QUEUED_CARD_STATE,
            startedAtMs: this.now(),
            lastProgressAtMs: null,
            expectedStartAtMs: null,
            watchdogHandle: null,
            settled: false,
        });
    }

    /** When this card's attempt sequence started — the retry budget's origin. */
    startedAtMs(fileUploadId: string): number {
        return this.cards.get(fileUploadId)?.startedAtMs ?? this.now();
    }

    /**
     * A 503 / 409 / transport failure. The card stays QUEUED — not "error" —
     * because the server took the request and asked us to wait, and a
     * `Retry-After` from ANY call pauses the whole batch (plan §4.2: per-file
     * backoff is useless when all 340 files talk to the same professorx).
     */
    onServerBusy(
        fileUploadId: string,
        info: { status?: number; retryAfterMs?: number | null; attempt?: number } = {},
    ): void {
        const record = this.cards.get(fileUploadId);
        if (!record) return;
        const first = record.state.status !== 'queued' && record.state.status !== 'retrying';
        this.apply(record, {
            type: 'server-busy',
            status: info.status,
            retryAfterMs: info.retryAfterMs ?? null,
        });
        if (info.retryAfterMs != null && info.retryAfterMs >= 0) {
            this.gate.pauseFor(info.retryAfterMs);
        }
        if (first) {
            this.emit(MetricEventName.SCHEDULE_QUEUED, {
                attempt: info.attempt ?? record.state.attempt,
                status: info.status ?? 0,
                reason: record.state.reason ?? 'server-busy',
            });
        }
    }

    /** The policy decided to retry: `attempt` is the attempt about to be made. */
    onRetryScheduled(
        fileUploadId: string,
        info: { attempt: number; delayMs: number; fromRetryAfter: boolean; reason: string; status?: number },
    ): void {
        const record = this.cards.get(fileUploadId);
        if (!record) return;
        this.apply(record, {
            type: 'retry-scheduled',
            attempt: info.attempt,
            delayMs: info.delayMs,
        });
        this.emit(MetricEventName.SCHEDULE_RETRY, {
            attempt: info.attempt,
            delayMs: info.delayMs,
            fromRetryAfter: info.fromRetryAfter,
            reason: info.reason,
            status: info.status ?? 0,
        });
    }

    /**
     * The schedule call succeeded. `expectedStartSeconds` is the server's
     * expected-start deadline; `dedup` is true when a RETRY came back 200 with
     * the workflowId the first attempt had already created (the M4 idempotency
     * replay, plan §3) — that is the metric that proves item B is working.
     */
    onScheduled(
        fileUploadId: string,
        info: { expectedStartSeconds?: number | null; attempt?: number; dedup?: boolean } = {},
    ): void {
        const record = this.cards.get(fileUploadId);
        if (!record) return;
        const seconds = typeof info.expectedStartSeconds === 'number'
            && Number.isFinite(info.expectedStartSeconds)
            && info.expectedStartSeconds > 0
            ? info.expectedStartSeconds
            : DEFAULT_EXPECTED_START_SECONDS;
        const expectedStartAtMs = this.now() + seconds * 1000;
        record.expectedStartAtMs = expectedStartAtMs;
        this.apply(record, { type: 'scheduled', expectedStartAtMs });
        if (info.dedup) {
            this.emit(MetricEventName.SCHEDULE_DEDUP_HIT, { attempt: info.attempt ?? record.state.attempt });
        }
        this.armWatchdog(fileUploadId, record);
    }

    /**
     * The 5-minute budget is spent (or the watchdog gave up): amber
     * "Not started — your file is still here" plus one batch-level Retry all.
     * The card and the uploaded bytes are KEPT — the reducer has no state that
     * can express otherwise.
     */
    onBudgetExhausted(fileUploadId: string, info: { attempt?: number; reason?: string } = {}): void {
        const record = this.cards.get(fileUploadId);
        if (!record) return;
        this.clearWatchdog(record);
        this.apply(record, { type: 'budget-exhausted' });
        this.emit(MetricEventName.SCHEDULE_STALLED, {
            attempt: info.attempt ?? record.state.attempt,
            reason: info.reason ?? 'retry-budget-exhausted',
        });
    }

    /** A progress message arrived (websocket or pod poll): work has STARTED. */
    onProgress(fileUploadId: string): void {
        const record = this.cards.get(fileUploadId);
        if (!record) return;
        const atMs = this.now();
        record.lastProgressAtMs = atMs;
        this.clearWatchdog(record);
        if (record.state.status !== 'idle') {
            this.apply(record, { type: 'progress', atMs });
        } else {
            record.state = { ...record.state, lastProgressAtMs: atMs, keepUploadedBytes: true };
        }
    }

    /** A terminal message arrived. The queued machine is done with this card. */
    onCompleted(fileUploadId: string): void {
        const record = this.cards.get(fileUploadId);
        if (!record) return;
        record.settled = true;
        this.clearWatchdog(record);
        this.apply(record, { type: 'completed', atMs: this.now() });
    }

    /** The user removed the card, or a terminal 4xx ended it. */
    forget(fileUploadId: string): void {
        const record = this.cards.get(fileUploadId);
        if (record) this.clearWatchdog(record);
        this.cards.delete(fileUploadId);
        this.nonces.forget(fileUploadId);
        this.notify();
    }

    // ------------------------------------------------------------ retry all

    /** `FileProcessService` installs the real re-dispatch here. */
    registerDispatcher(dispatcher: ScheduleDispatcher | null): void {
        this.dispatcher = dispatcher;
    }

    /**
     * The ONE batch-level control (plan §4.4). Mints a new nonce per card so the
     * server treats each as a genuinely new attempt rather than replaying the
     * stored failure, lifts the shared pause, and re-dispatches.
     */
    retryAll(): string[] {
        const ids = this.stalledCardIds();
        if (ids.length === 0) return ids;
        this.emit(MetricEventName.SCHEDULE_RETRY_ALL, { cards: ids.length });
        ids.forEach(id => {
            this.renewNonce(id);
            const record = this.cards.get(id);
            if (!record) return;
            record.startedAtMs = this.now();
            record.expectedStartAtMs = null;
            record.settled = false;
            this.apply(record, { type: 'user-retry-all' });
        });
        this.gate.resume();
        const dispatcher = this.dispatcher;
        if (dispatcher) ids.forEach(id => dispatcher(id));
        return ids;
    }

    // ------------------------------------------------------------ watchdog

    /**
     * Plan §4.3 / correction 22: nothing on the client today concludes
     * "scheduled but never started" — the pod poll simply never gives up. This
     * timer is that conclusion, and it only ever answers "did work START",
     * never "is this slow": a long conversion is legitimately quiet.
     */
    private armWatchdog(fileUploadId: string, record: CardRecord): void {
        this.clearWatchdog(record);
        if (record.expectedStartAtMs === null) return;
        const wait = Math.max(0, record.expectedStartAtMs - this.now());
        record.watchdogHandle = this.setTimer(() => {
            record.watchdogHandle = null;
            const current = this.cards.get(fileUploadId);
            if (!current || current.settled) return;
            const verdict = this.watchdog.check({
                startedAtMs: current.startedAtMs,
                expectedStartAtMs: current.expectedStartAtMs,
                lastProgressAtMs: current.lastProgressAtMs,
            });
            if (verdict.status !== 'stalled') return;
            this.apply(current, { type: 'expected-start-passed', atMs: this.now() });
            this.emit(MetricEventName.SCHEDULE_STALLED, {
                attempt: current.state.attempt,
                reason: verdict.reason,
            });
        }, wait);
    }

    private clearWatchdog(record: CardRecord): void {
        if (record.watchdogHandle !== null && record.watchdogHandle !== undefined) {
            this.clearTimer(record.watchdogHandle);
            record.watchdogHandle = null;
        }
    }

    // --------------------------------------------------------------- internals

    private apply(record: CardRecord, event: Parameters<typeof queuedCardReducer>[1]): void {
        const next = queuedCardReducer({ ...record.state, lastProgressAtMs: record.lastProgressAtMs }, event);
        if (next === record.state) return;
        record.state = next;
        this.notify();
    }

    private notify(): void {
        this.listeners.forEach(listener => {
            try { listener(); } catch { /* a bad listener must not wedge the batch */ }
        });
    }

    /** Tests only: drop every card, nonce and timer. */
    reset(): void {
        this.cards.forEach(record => this.clearWatchdog(record));
        this.cards.clear();
        this.nonces.allKeys().forEach(key => {
            const at = key.indexOf(':');
            this.nonces.forget(at > 0 ? key.slice(0, at) : key);
        });
        this.gate.resume();
        this.dispatcher = null;
        this.enabled = false;
        this.setTier('FREE');
        this.gate.setHidden(this.visibilityDoc?.hidden === true);
    }
}

/**
 * The copy layer. Plan §4.4 fixes the two strings the user actually reads:
 * "Queued — server busy, retrying" while we are still trying, and the amber
 * "Not started — your file is still here" once we have stopped — the second
 * half of that sentence is the entire point of correction 16, since the old
 * path had already deleted the bytes by the time it said anything.
 */
/** Code 10 (c), ENFORCEMENT-POLICY.md §6.5 "paced" (owner wording 2026-10-12). */
export const SCHEDULE_PACED_COPY = 'Not started yet — paced. Press Retry all when you\'re ready.';

export function scheduleCardMessage(state: QueuedCardState): string {
    switch (state.status) {
        case 'queued':
            // Round 6 (Studio I4, QA#10): `queued` is ALSO the state a card enters when the
            // schedule call SUCCEEDED (`scheduled` → `reason: 'scheduled'`, and `user-retry-all`),
            // and every normal job wore "server busy, retrying" for the ~2.5 s before its first
            // progress frame. Only a server-busy / in-flight answer is a retry story.
            return state.reason === 'server-busy' || state.reason === 'schedule-in-flight'
                ? 'Queued — server busy, retrying'
                : 'Queued — starting soon';
        case 'retrying':
            return state.attempt > 0
                ? `Queued — server busy, retrying (${state.attempt} of ${state.maxAttempts})`
                : 'Queued — server busy, retrying';
        case 'stalled':
        case 'retry-all-available':
            // Code 10 (c): after the 5-minute budget the card stays amber; the owner wants an explicit
            // press of the batch-level "Retry all", so the line says so.
            return SCHEDULE_PACED_COPY;
        default:
            return '';
    }
}

/**
 * The tab-wide singleton. One gate, one nonce registry and one state map for
 * every card on the page — the Retry-After pause is only "shared" if there is
 * exactly one of it (plan §4.2).
 */
export const scheduleCoordinator = new ScheduleCoordinator();
